import React from 'react';
import { Button, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import { dialogButtonRadius, StyledDialog } from 'theme/index';
import COLORS from 'theme/colors';

interface SingleActionDialogProps {
  open: boolean;
  title: string;
  message: string;
  // Called for both the OK button and closing the dialog.
  onOk: () => void;
  okLabel?: string;
}

/**
 * A message dialog with one OK action, laid out to stay aligned on phones and desktop browsers -
 * the shared layout behind InPersonLocationRestrictedDialog and ProviderUnavailableDialog.
 *
 * Overrides the theme's MuiDialogTitle lineHeight (3.2 - fine for a short one-line title, but a
 * wrapping title then gets huge gaps between lines on a phone) and centres title, message and
 * button together (the theme centres only the title). The OK button is full width on phones and
 * a fixed, centred width from `sm` up - same pattern as Dashboard/DashboardNextStepDialog. The
 * button is pill-shaped (dialogButtonRadius), the same as the other self-scheduling dialogs.
 */
const SingleActionDialog = ({ open, title, message, onOk, okLabel = 'OK' }: SingleActionDialogProps) => (
  <StyledDialog open={open} onClose={onOk} maxWidth='xs' fullWidth>
    <DialogTitle
      sx={{
        fontSize: { xs: '1.15rem', sm: '1.35rem' },
        lineHeight: 1.3,
        textAlign: 'center',
        px: { xs: 1, sm: 2 },
        pt: { xs: 1, sm: 1.5 },
        pb: 1,
      }}
    >
      {title}
    </DialogTitle>
    <DialogContent sx={{ px: { xs: 1, sm: 2 }, pb: 1 }}>
      <Typography sx={{ color: COLORS.Text, textAlign: 'center' }}>{message}</Typography>
    </DialogContent>
    <DialogActions sx={{ justifyContent: 'center', px: { xs: 1, sm: 2 }, pb: { xs: 1, sm: 1.5 } }}>
      <Button
        variant='contained'
        fullWidth
        onClick={onOk}
        sx={{ textTransform: 'none', maxWidth: { sm: 200 }, borderRadius: dialogButtonRadius }}
      >
        {okLabel}
      </Button>
    </DialogActions>
  </StyledDialog>
);

export default SingleActionDialog;
